import * as THREE from 'three';
import { GTAOPass } from 'three/examples/jsm/postprocessing/GTAOPass.js';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';
import { swapMaterials } from './materialSwap';

const RADIUS_RATIO = 0.06;
const RADIUS_TOLERANCE = 1e-6;
const AO_SAMPLES = 24;
const DISTANCE_EXPONENT = 2;
const SCALE = 1.6;
const CLIP_BOX_RADII = 4;
const NORMALS_CLEAR = 0x7777ff;
// Edge-aware blur that smooths the sampling noise without bleeding across silhouettes.
const DENOISE = {
  lumaPhi: 10,
  depthPhi: 2,
  normalPhi: 3,
  radius: 6,
  radiusExponent: 1,
  rings: 2,
  samples: 16,
};

const QUAD_VERTEX = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Multiplied into the sRGB frame as it is, which darkens like the factor raised to 2.2 in linear
// light: creases go near black instead of fading out.
const MULTIPLY_FRAGMENT = /* glsl */ `
  uniform sampler2D tOcclusion;
  varying vec2 vUv;
  void main() {
    gl_FragColor = vec4(vec3(texture2D(tOcclusion, vUv).r), 1.0);
  }
`;

/**
 * Darkens creases and corners of the part over the frame already drawn. The occlusion is computed
 * from the part alone, so the floor, grid, plate and shadow never darken it, and everything else
 * stays exactly as drawn.
 */
export class AmbientOcclusion {
  private readonly gtao: GTAOPass;
  private readonly gbuffer: THREE.WebGLRenderTarget;
  private readonly occlusion: THREE.WebGLRenderTarget;
  private readonly multiply: FullScreenQuad;
  // Both sides: imported meshes often have faces the wrong way round, which would let it see through them.
  private readonly normals = new THREE.MeshNormalMaterial({ blending: THREE.NoBlending, side: THREE.DoubleSide });
  private readonly clearColor = new THREE.Color();
  private radius = 0;

  constructor(private readonly renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera) {
    const size = renderer.getDrawingBufferSize(new THREE.Vector2());
    const depthTexture = new THREE.DepthTexture(size.x, size.y);
    depthTexture.format = THREE.DepthStencilFormat;
    depthTexture.type = THREE.UnsignedInt248Type;
    this.gbuffer = new THREE.WebGLRenderTarget(size.x, size.y, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      type: THREE.HalfFloatType,
      depthTexture,
    });
    this.occlusion = new THREE.WebGLRenderTarget(size.x, size.y);
    // three r183's GTAOPass throws when its constructor is given a G-buffer: setGBuffer then reads a
    // render target that only exists without one. So the pass is built first and given ours after.
    this.gtao = new GTAOPass(scene, camera, size.x, size.y);
    this.gtao.setGBuffer(depthTexture, this.gbuffer.texture);
    this.gtao.updatePdMaterial(DENOISE);
    this.gtao.output = GTAOPass.OUTPUT.Denoise;
    this.multiply = new FullScreenQuad(
      new THREE.ShaderMaterial({
        uniforms: { tOcclusion: { value: this.occlusion.texture } },
        vertexShader: QUAD_VERTEX,
        fragmentShader: MULTIPLY_FRAGMENT,
        blending: THREE.CustomBlending,
        blendSrc: THREE.ZeroFactor,
        blendDst: THREE.SrcColorFactor,
        depthTest: false,
        depthWrite: false,
      }),
    );
  }

  setSize(width: number, height: number): void {
    const ratio = this.renderer.getPixelRatio();
    const w = Math.floor(width * ratio);
    const h = Math.floor(height * ratio);
    this.gbuffer.setSize(w, h);
    this.occlusion.setSize(w, h);
    this.gtao.setSize(w, h);
  }

  /** Multiplies the part's occlusion into the frame on screen. */
  render(camera: THREE.Camera, model: THREE.Object3D, box: THREE.Box3, clip: THREE.Plane | null): void {
    this.drawNormals(camera, model, clip);
    this.useCamera(camera);
    const radius = box.getSize(new THREE.Vector3()).length() * RADIUS_RATIO;
    if (Number.isFinite(radius) && radius > 0 && Math.abs(radius - this.radius) > radius * RADIUS_TOLERANCE) {
      this.radius = radius;
      this.gtao.updateGtaoMaterial({
        radius,
        thickness: radius,
        distanceExponent: DISTANCE_EXPONENT,
        distanceFallOff: 1,
        scale: SCALE,
        samples: AO_SAMPLES,
        screenSpaceRadius: false,
      });
      this.gtao.setSceneClipBox(box.clone().expandByScalar(radius * CLIP_BOX_RADII));
    }
    const target = this.renderer.getRenderTarget();
    this.gtao.render(this.renderer, this.occlusion, this.occlusion, 0, false);
    this.renderer.setRenderTarget(target);
    this.multiply.render(this.renderer);
  }

  dispose(): void {
    this.gtao.dispose();
    this.occlusion.dispose();
    this.multiply.material.dispose();
    this.multiply.dispose();
    this.gbuffer.depthTexture?.dispose();
    this.gbuffer.dispose();
    this.normals.dispose();
  }

  // GTAOPass reads the projection kind only when it is built; the view switches it at will.
  private useCamera(camera: THREE.Camera): void {
    this.gtao.camera = camera;
    const material = this.gtao.gtaoMaterial;
    const perspective = (camera as THREE.PerspectiveCamera).isPerspectiveCamera ? 1 : 0;
    if (material.defines.PERSPECTIVE_CAMERA === perspective) return;
    material.defines.PERSPECTIVE_CAMERA = perspective;
    material.needsUpdate = true;
  }

  private drawNormals(camera: THREE.Camera, model: THREE.Object3D, clip: THREE.Plane | null): void {
    const { renderer } = this;
    this.normals.clippingPlanes = clip ? [clip] : null;
    const previousTarget = renderer.getRenderTarget();
    renderer.getClearColor(this.clearColor);
    const previousAlpha = renderer.getClearAlpha();
    renderer.setRenderTarget(this.gbuffer);
    renderer.setClearColor(NORMALS_CLEAR, 1);
    renderer.clear();
    const restore = swapMaterials(model, this.normals);
    try {
      renderer.render(model, camera);
    } finally {
      restore();
      renderer.setRenderTarget(previousTarget);
      renderer.setClearColor(this.clearColor, previousAlpha);
    }
  }
}
